import { Component, OnDestroy, OnInit } from '@angular/core';
import { forkJoin, Observable } from 'rxjs';
import { SettingsService } from '../../services/settings.service';
import { SprintService } from '../../services/sprint.service';
import { Sprint } from '../../models/sprint';
import { Status } from '../../models/status';
import { ProductBacklogComponent } from '../../components/product-backlog/product-backlog.component';
import { AwallComponentEvent } from '../../models/awallComponentEvent';
import { ComponentEvent } from '../../models/enums/componentEvent';
import { SprintcardHelper } from '../../components/sprintcard/sprintcard.helper';
import { ReleaseplanningSynchroHelper } from './release-planning-synchro-helper';
import { NotifierService } from 'angular-notifier';
import { SettingsEventType } from '../../models/enums/settingsEventType';
import { BoardData } from '../../models/boardData';
import { BaseBoardComponent } from '../baseBoard';
import { CommunicationService } from '../../services/communication.service';
import { ProjectService } from '../../services/project.service';

@Component({
  selector: 'app-release-planning-view',
  templateUrl: './release-planning-view.component.html',
  styleUrls: ['./release-planning-view.component.scss']
  })

/** Class representing the view of release planning. */
export class ReleasePlanningViewComponent extends BaseBoardComponent implements OnInit, OnDestroy {
  private projectKey: string;
  public columnStatuses: Status[] = [];

  // array to save all sprints fetched from Jira
  public sprints: Sprint[] = [];
  public scrollButtonLeftDisabled = true;
  public scrollButtonRightDisabled = false;
  public static readonly componentId = 'ReleasePlanningViewComponent';

  constructor(
    private communicationService: CommunicationService,
    private settingsService: SettingsService,
    private sprintService: SprintService,
    private projectService: ProjectService,
    private sprintCardHelper: SprintcardHelper,
    private syncHelper: ReleaseplanningSynchroHelper,
    private notifier: NotifierService,
  ) {
    super();
  }

  ngOnInit() {
    this.syncHelper.initSyncEventListeners(this);
    this.communicationService.registerComponent(ReleasePlanningViewComponent.componentId, this);
    this.subscriptions.push(this.settingsService.getUpdateObservable().subscribe(settingsEventType => {
      if (settingsEventType === SettingsEventType.PROJECT_CHANGED) {
        this.updateViewData();
      }
    }));
  }

  ngOnDestroy(): void {
    this.communicationService.unregisterComponent(ReleasePlanningViewComponent.componentId);
    for (const ev of this.subscriptions) {
      ev.unsubscribe();
    }
  }

  /**
   * Loads sprints, stories and backlog for the given project. Updates the UI accordingly
   **/
  updateViewData() {
    this.clearViewData();
    this.projectKey = this.settingsService.getProjectName();

    if (!this.projectKey) {
      return;
    }
    this.notifier.notify('info', 'Loading Data...');

    forkJoin([
      this.sprintService.getSprints(this.projectKey),
      this.projectService.getBoardStates(this.projectKey)
    ]).subscribe(([sprintData, boardConfiguration]) => {
      this.columnStatuses = boardConfiguration.getAllStatuses();
      this.addSprints(sprintData);
    });
  }

  onCommunicationEvent(event: AwallComponentEvent): void {
    super.onCommunicationEvent(event);
  }

  /**
 * Clear all data held by this view. This will result in an empty board.
 */
  private clearViewData() {
    this.sprints = [];
    this.columnStatuses = [];
  }

  public openCardForNewSprint(mouseEvent: MouseEvent): void {
    this.sprintCardHelper.openNewSprintDialog(this.projectKey, (sprint: Sprint) => this.onSprintCreated(sprint), mouseEvent, null);
  }

  public onSprintCreated(newSprint: Sprint): void {
    if (newSprint) {
      this.addSprints(new Array(newSprint));
    }
  }

  /**
   * Remove sprint from our datamodel and reload backlogdata, as issues of deleted sprint might have moved
   * to backlog.
   */
  onSprintRemoved(sprintId: number): void {
    const sprint = this.sprints.find(s => (s.id === sprintId));
    if (sprint && this.sprints.indexOf(sprint) >= 0) {
      this.sprints.splice(this.sprints.indexOf(sprint), 1);
      const event = new AwallComponentEvent(ComponentEvent.BACKLOG_REQUEST_UPDATE, '');
      this.communicationService.sendData(ProductBacklogComponent.componentId, event);
    }
  }

  public getProjectName(): string {
    return this.settingsService.getProjectName();
  }

  onColumnViewScroll(): void {
    const scrollContainer = document.querySelector<HTMLElement>('#releaseplanning-column-view');
    this.scrollButtonLeftDisabled = (scrollContainer.scrollLeft === 0);
    this.scrollButtonRightDisabled = (scrollContainer.offsetWidth + Math.floor(scrollContainer.scrollLeft) >= scrollContainer.scrollWidth);
  }

  /**
   * @param scrollLeft if true perform a scroll-action to the left, if false to the right
   */
  onScrollButtonClicked(scrollLeft: boolean): void {
    const container: HTMLElement = document.querySelector('#releaseplanning-column-view');
    const numberOfSprints = this.sprints.length;
    const scrollAmount = (container.scrollWidth / numberOfSprints);

    if (scrollLeft) {
      container.scrollLeft -= scrollAmount;
    }
    else {
      container.scrollLeft += scrollAmount;
    }
  }

  private addSprints(sprints: Sprint[]): void {
    this.sprints.push(...sprints);
    // Scroll to the right with a delay to give the DOM some time to create the elements of the added sprints
    setTimeout(() => this.scrollToLastSprint(), 300);
  }

  /**
   * Set the horizontal scroll all the way to the right so the last sprint is visible
   */
  private scrollToLastSprint(): void {
    const container: HTMLElement = document.querySelector('#releaseplanning-column-view');
    container.scrollLeft = container.scrollWidth;
  }

  getBoardData(): Observable<BoardData> {
    throw new Error('Method not implemented.');
  }

}
